<template>
  <div class="pc-dept-select row flex-center" v-if="!hideleftTitle">
    <div class="font-666 bg-white left-label">{{ leftTitle }}</div>
    <el-tree-select
      ref="currentSelect"
      class="fw100"
      v-model="self[modelval]"
      :data="deptTreeOptions"
      :props="treeProps"
      :node-key="value"
      :clearable="clearable"
      :filterable="filterable"
      :placeholder="placeholder"
      :loading="loading"
      :filter-node-method="filterDeptNode"
      check-strictly
      default-expand-all
      v-bind="$attrs"
      @focus="handleSelectFocus"
      @clear="handleClear"
      @change="handleChange"
    />
  </div>
  <div v-else>
    <!-- 只展示部门树选择器，不展示左侧标题 -->
    <el-tree-select
      ref="currentSelect"
      class="fw100"
      v-model="self[modelval]"
      :data="deptTreeOptions"
      :props="treeProps"
      :node-key="value"
      :clearable="clearable"
      :filterable="filterable"
      :placeholder="placeholder"
      :loading="loading"
      :filter-node-method="filterDeptNode"
      check-strictly
      default-expand-all
      v-bind="$attrs"
      @focus="handleSelectFocus"
      @clear="handleClear"
      @change="handleChange"
    />
  </div>
</template>

<script>
import { getPublickOptions } from "@/util/getoptions.js";
import PinyinMatch from "pinyin-match";

/** 部门树下拉固定使用企微部门数据源 */
const DEPT_OPTION_TYPE_LIST = ["qwDepartment"];

export default {
  name: "pcDeptSelect",
  inheritAttrs: false,
  props: {
    self: {
      type: Object,
      required: true,
    },
    modelval: {
      type: String,
      required: true,
    },
    leftTitle: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "请选择",
    },
    label: {
      type: String,
      default: "departmentName",
    },
    value: {
      type: String,
      default: "id",
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    filterable: {
      type: Boolean,
      default: true,
    },
    hideleftTitle: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["change", "clear"],
  data() {
    return {
      deptTreeOptions: [],
      loading: false,
      isLoaded: false,
      /** 并发调用 loadDeptTree / initDeptSelectAfterLoad 时复用同一次请求 Promise */
      deptTreeLoadPromise: null,
    };
  },
  computed: {
    treeProps() {
      return {
        children: "children",
        label: this.label,
      };
    },
  },
  mounted() {
    if (this.self[this.modelval]) {
      this.loadDeptTree();
    }
  },
  methods: {
    /**
     * 拉取企微部门树；多次并发 await 会落到同一次请求上，避免 mounted 与外部 init 重复请求或提前 return。
     */
    async loadDeptTree() {
      if (this.isLoaded) return;
      if (this.deptTreeLoadPromise) {
        await this.deptTreeLoadPromise;
        return;
      }
      this.loading = true;
      this.deptTreeLoadPromise = (async () => {
        try {
          const [deptTreeList] = await getPublickOptions(DEPT_OPTION_TYPE_LIST);
          // 递归清理空 children，避免树选择器把叶子节点识别成可展开节点。
          this.deptTreeOptions = this.normalizeDeptTree(deptTreeList || []);
          this.isLoaded = true;
        } catch (error) {
          this.deptTreeOptions = [];
          this.$message.error("部门选项加载失败，请稍后重试");
        } finally {
          this.loading = false;
          this.deptTreeLoadPromise = null;
        }
      })();
      await this.deptTreeLoadPromise;
    },
    /**
     * 在部门树中递归查找与外部 id 一致的节点，返回树节点上的真实 value（兼容 number / string，与 el-tree-select 一致）。
     */
    findMatchedDeptValueFromTree(deptList, outsideDeptId) {
      let matchedDeptValue = undefined;
      const outsideDeptIdStr = String(outsideDeptId);
      const walkDeptTree = (nodes) => {
        if (!Array.isArray(nodes) || matchedDeptValue !== undefined) return;
        for (let deptIndex = 0; deptIndex < nodes.length; deptIndex += 1) {
          const deptNode = nodes[deptIndex];
          const nodeKeyVal = deptNode[this.value];
          if (nodeKeyVal != null && String(nodeKeyVal) === outsideDeptIdStr) {
            matchedDeptValue = nodeKeyVal;
            return;
          }
          if (deptNode.children?.length) {
            walkDeptTree(deptNode.children);
          }
        }
      };
      walkDeptTree(deptList);
      return matchedDeptValue;
    },
    /**
     * 部门树加载完成后与外部绑定值对齐：
     * - 若当前 deptId 在树中能找到：选中并修正为树节点上的类型（避免接口 string 与树 number 不一致导致不回显）
     * - 若找不到：将外部绑定清空，避免带着无效 departmentId 去查列表
     * 父组件应在设置好默认部门后 await 本方法，再发起列表请求。
     */
    async initDeptSelectAfterLoad() {
      await this.loadDeptTree();
      const currentDeptId = this.self[this.modelval];
      // 无默认值时仅保证树已加载，不修改绑定
      if (currentDeptId === "" || currentDeptId == null) return;
      if (!this.isLoaded) return;
      const matchedDeptValue = this.findMatchedDeptValueFromTree(
        this.deptTreeOptions,
        currentDeptId,
      );
      if (matchedDeptValue === undefined) {
        // 接口给的部门 id 在可选树中不存在，视为无部门条件
        this.self[this.modelval] = "";
      } else {
        this.self[this.modelval] = matchedDeptValue;
      }
    },
    normalizeDeptTree(deptList) {
      if (!Array.isArray(deptList)) return [];
      return deptList.map((deptItem) => {
        const deptNode = { ...deptItem };
        if (Array.isArray(deptNode.children) && deptNode.children.length) {
          deptNode.children = this.normalizeDeptTree(deptNode.children);
        } else {
          delete deptNode.children;
        }
        return deptNode;
      });
    },
    handleSelectFocus() {
      this.loadDeptTree();
    },
    handleClear() {
      this.$emit("clear");
    },
    handleChange(deptId) {
      this.$emit("change", { value: deptId });
    },
    filterDeptNode(keyword, deptNode) {
      if (!keyword) return true;
      const deptName = deptNode?.[this.label] || "";
      return !!PinyinMatch.match(deptName, keyword);
    },
    selectblur() {
      const selectRef = this.$refs.currentSelect;
      if (selectRef) {
        selectRef.blur();
      }
    },
    clear() {
      this.deptTreeOptions = [];
      this.isLoaded = false;
      this.deptTreeLoadPromise = null;
    },
  },
};
</script>

<style scoped lang="scss">
.pc-dept-select {
  :deep(.el-select__wrapper) {
    height: 34px;
    box-sizing: border-box;
    border: 1px solid #e6e6e6;
    border-left: 0;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    box-shadow: none !important;

    &.is-focused {
      box-shadow: none !important;
    }
  }

  .left-label {
    height: 34px;
    padding: 0 10px;
    box-sizing: border-box;
    color: #666;
    font-size: 14px;
    line-height: 32px;
    white-space: nowrap;
    border: 1px solid #e6e6e6;
    border-right: 0;
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
  }
}
</style>